import { useFragment } from "@/generated";
import {
  DeleteSlidesDocument,
  type LinkedCopy,
  type SlideFieldsFragment,
  SlideFieldsFragmentDoc,
} from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { ApolloCache, Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

export function updateDeletedSlidesInCache(cache: ApolloCache, slides: SlideFieldsFragment[]) {
  cache.batch({
    update(cache) {
      for (const slide of slides) {
        if (!slide.isDeleted) continue;

        const cacheId = cache.identify(slide);
        if (cacheId) cache.evict({ id: cacheId });

        cache.modify({
          id: `Lesson:${slide.lessonId}`,
          fields: {
            trashCount(existingData = 0) {
              return existingData - 1;
            },
          },
        });

        // Remove from original item's linkedCopies if this is a linked copy
        if (slide.isLinkedCopy && slide.originalSlideId) {
          const linkedId = cache.identify({ __typename: "LinkedCopy", id: slide.id });

          if (linkedId) {
            cache.evict({ id: linkedId });
          }
        }
      }
      cache.gc();
    },
  });
}

/**
 * Hook for deleting slides and updating the Apollo cache.
 *
 * @example
 * const { deleteSlides, loading } = useMoveSlides();
 * const deletedSlides = await deleteSlides(["slide1", "slide2"]);
 */
export function useDeleteSlides() {
  const [deleteSlidesMutation, { loading, error }] = useMutation(DeleteSlidesDocument, {
    update(cache, { data }) {
      if (!data?.deleteSlides) return;

      const slides = data.deleteSlides.map((slide) => useFragment(SlideFieldsFragmentDoc, slide));

      try {
        updateDeletedSlidesInCache(cache, slides);
      } catch (error) {
        console.error("Cache update failed:", error);
      }
    },
  });

  const deleteSlides = async (slideIds: string[], skipCheck: boolean) => {
    return withToast(
      async () => {
        const result = await deleteSlidesMutation({
          variables: {
            slideIds,
            skipCheck,
          },
        });

        const deletedSlides = result.data?.deleteSlides ?? [];

        return deletedSlides;
      },
      {
        loading: `Deleting ${slideIds.length} slide${slideIds.length !== 1 ? "s" : ""}...`,
        success: `Successfully deleted ${slideIds.length} slide${slideIds.length !== 1 ? "s" : ""}`,
        error: "Failed to delete slides. Please try again.",
      },
      "deleteSlides"
    );
  };

  return {
    deleteSlides,
    loading,
    error,
  };
}
